import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import currencyFormatter from 'currency-formatter';
import React, { PureComponent } from 'react';

interface IProps {
  id?: string;
  onChange?: any;
  mask: 'phone' | 'creditCard' | 'monthYear' | 'cpfCnpj' | 'postalCode' | 'number' | 'money' | 'singledigit' | 'percent';
  value?: string | number;
  label?: string;
  placeHolder?: string;
  margin?: any;
  style?: any;
  startAdornment?: any;
  endAdornment?: any;
  helperText?: string;
  className?: string;
  disabled?: boolean;
  disableUnderline?: boolean;
  defaultValue?: any;
  InputProps?: any;
  InputLabelProps?: any;
  inputProps?: any;
  fullWidth?: any;
  error?: any;
  onBlur?: any;
  name?: any;
  onFocus?: any;
  noStrictMaxPercent?: boolean;
}

interface IState {
  value: string | number;
}

class CustomMaskedInput extends PureComponent<IProps, IState> {
  private readonly masks: any = {
    phone: {
      apply: (value: string) => {
        if (!value) return value;

        const regexp = value.length > 10 ? /^(\d{0,2})(\d{0,5})(\d{0,4}).*/ : /^(\d{0,2})(\d{0,4})(\d{0,4}).*/;

        const result = value.length > 2 ? '($1) $2-$3' : '($1$2$3';

        return value.replace(regexp, result).replace(/-$/, '');
      },
      clean: (value: string) => value.replace(/\D/gi, '').substr(0, 11),
    },
    creditCard: {
      apply: (value: string) => {
        if (!value) return value;
        const regexp = /^(\d{0,4})?(\d{0,4})?(\d{0,4})?(\d{0,4})?/;
        return value.replace(regexp, '$1 $2 $3 $4').replace(/-$/, '').trim();
      },
      clean: (value: string) => value.replace(/\D/gi, '').substr(0, 16),
    },
    monthYear: {
      apply: (value: string) => {
        if (!value) return value;
        const regexp = /^(\d{2})(\d{0,2})?/;

        return value.replace(/\D/, '').replace(regexp, '$1/$2').replace(/-$/, '').trim();
      },
      clean: (value: string) => value.substr(0, 5),
    },
    cpfCnpj: {
      apply: (value: string) => {
        if (!value) return value;

        const regexp = value.length <= 11 ? /^(\d{0,3})(\d{0,3})(\d{0,3})(\d{0,2})/ : /^(\d{0,2})(\d{0,3})(\d{0,3})(\d{0,4})(\d{0,2})/;

        const result =
          value.length < 4
            ? '$1'
            : value.length < 7
            ? '$1.$2'
            : value.length < 10
            ? '$1.$2.$3'
            : value.length <= 11
            ? '$1.$2.$3-$4'
            : '$1.$2.$3/$4-$5';

        return value.replace(regexp, result).replace(/-$/, '');
      },
      clean: (value: string) => value.replace(/\D/gi, '').substr(0, 14),
    },
    postalCode: {
      apply: (value: string) => {
        if (!value) return value;
        const regexp = /^(\d{5})(\d{3})?/;

        return value.replace(/\D/, '').replace(regexp, '$1-$2').replace(/-$/, '').trim();
      },
      clean: (value: string) => value.substr(0, 9),
    },
    number: {
      apply: (value: string) => {
        if (!value) return '';

        return value.replace(/\D/g, '');
      },
      clean: (value: string) => value.replace(/\D/g, ''),
    },
    money: {
      apply: (value: string) => {
        if (!value) return '0,00';
        value = value.toString();
        if (value.match(/^\d+$/)) {
          value = `${value}.00`;
        }
        if (value.match(/^\d+\.\d+$/)) {
          value = Number(value).toFixed(2);
        }
        value = value.replace(/\D+/gi, '');
        const intval = value.substr(0, value.length - 2);
        const cents = value.substr(value.length - 2, 2);
        value = `${intval}.${cents}`;
        return currencyFormatter.format(Number(value), { code: 'BRL', precision: 2, symbol: '' });
      },
      clean: (value: string) => value,
    },
    singledigit: {
      apply: (value: string) => {
        if (!value) return value;

        return value.replace(/\D/g, '');
      },
      clean: (value: string) => value.substr(0, 1),
    },
    percent: {
      apply: (value: string) => {
        if (!value) return value;
        value = value.toString();
        if (value.match(/^\d+$/)) {
          value = `${value}.00`;
        }
        if (value.match(/^\d+\.\d+$/)) {
          value = Number(value).toFixed(2);
        }
        value = value.replace(/\D+/gi, '');
        const intval = value.substr(0, value.length - 2);
        const cents = value.substr(value.length - 2, 2);
        value = `${intval}.${cents}`;
        if (parseInt(value) > 100 && !this.props.noStrictMaxPercent) {
          return '100';
        }
        return currencyFormatter.format(Number(value), { code: 'BRL', precision: 2, symbol: '' });
      },
      clean: (value: string) => value,
    },
  };

  constructor(props: IProps) {
    super(props);
    this.state = {
      value: this.masks[props.mask].apply((props.value && props.value.toString()) || ''),
    };
  }

  /* the onChange of this component returns the value itself not the event */
  handleOnchage = (event: any) => {
    const clean = this.masks[this.props.mask].clean(event.target.value);
    const masked = this.masks[this.props.mask].apply(clean);
    this.setState({ value: masked });
    this.props.onChange(masked);
  };

  componentDidUpdate() {
    const { mask, value } = this.props;

    if (this.state.value !== value) {
      if (value) {
        const clean = this.masks[mask].clean(value.toString());
        const masked = this.masks[mask].apply(clean);
        this.setState({ value: masked });
      }
    }
  }

  componentDidMount() {
    const { mask, value } = this.props;

    if (value) {
      const clean = this.masks[mask].clean(value.toString());
      const masked = this.masks[mask].apply(clean);
      this.setState({ value: masked });
    }
  }

  render() {
    const { startAdornment, endAdornment } = this.props;
    let inputprops = { disableUnderline: true };

    if (startAdornment) inputprops['startAdornment'] = <InputAdornment position='start'>{startAdornment}</InputAdornment>;

    if (endAdornment) inputprops['endAdornment'] = <InputAdornment position='end'>{endAdornment}</InputAdornment>;

    return (
      <TextField
        id={this.props.id}
        InputProps={inputprops}
        name={this.props.name}
        onFocus={this.props.onFocus}
        error={this.props.error}
        value={this.state.value}
        disabled={this.props.disabled}
        onChange={this.handleOnchage}
        label={this.props.label}
        placeholder={this.props.placeHolder}
        margin={this.props.margin}
        helperText={this.props.helperText}
        className={this.props.className}
        defaultValue={this.props.defaultValue}
        InputLabelProps={this.props.InputLabelProps}
        fullWidth={this.props.fullWidth}
        onBlur={this.props.onBlur}
        inputProps={this.props.inputProps}
      />
    );
  }
}

export default CustomMaskedInput;
